import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, CheckCircle2, 
  Package, Search, Save, Loader2,
  Box, Info
} from 'lucide-react';
import { incomingService, locationService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import './IncomingAddressingPage.css';

interface AddressingItem {
  id: number;
  product_id: number;
  product_name?: string;
  sku?: string;
  quantity: number;
  lot?: string;
  expiry?: string;
  suggestedLocation?: string;
  suggestedLocationId?: number;
  actualLocation: string;
  actualLocationId?: number;
  isAddressed: boolean;
}

const IncomingAddressingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<AddressingItem[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [invoiceEntry, setInvoiceEntry] = useState<any>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Carregar nota fiscal e itens
      const invoiceResponse = await incomingService.getById(parseInt(id));
      if (invoiceResponse.success && invoiceResponse.data) {
        setInvoiceEntry(invoiceResponse.data);
        
        // Carregar itens da nota fiscal
        const itemsResponse = await incomingService.getItems(parseInt(id));
        if (itemsResponse.success && itemsResponse.data) {
          const itemsData = itemsResponse.data.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name || item.xml_description || 'Produto sem nome',
            sku: item.sku || item.supplier_code || 'N/A',
            quantity: parseFloat(item.quantity) || 0,
            lot: item.lot || '',
            expiry: item.expiry || '',
            suggestedLocation: item.location_code || '',
            suggestedLocationId: item.location_id || null,
            actualLocation: item.location_code || '',
            actualLocationId: item.location_id || null,
            isAddressed: !!item.location_id
          }));
          setItems(itemsData);
        }
      }

      // Carregar endereços disponíveis
      const locationsResponse = await locationService.getAll({ status: 'active' });
      if (locationsResponse.success) {
        setLocations(locationsResponse.data || []);
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      showToast('Erro ao carregar dados para endereçamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateLocation = (itemId: number, locationCode: string) => {
    const newItems = items.map(item => {
      if (item.id === itemId) {
        const location = locations.find(loc => loc.code === locationCode.toUpperCase().trim());
        const trimmedCode = locationCode.toUpperCase().trim();
        
        return {
          ...item,
          actualLocation: trimmedCode,
          actualLocationId: location ? location.id : null,
          isAddressed: trimmedCode.length > 0 && !!location
        };
      }
      return item;
    });
    setItems(newItems);
  };

  const handleUseSuggested = (itemId: number) => {
    const newItems = items.map(item => {
      if (item.id === itemId && item.suggestedLocation) {
        return {
          ...item,
          actualLocation: item.suggestedLocation,
          actualLocationId: item.suggestedLocationId || null,
          isAddressed: true
        };
      }
      return item;
    });
    setItems(newItems);
  };

  const handleFinish = async () => {
    if (addressedCount < items.length) {
      showToast('Todos os itens devem ser endereçados antes de concluir', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      // Salvar endereçamento de cada item
      const updates = items.map(item => ({
        itemId: item.id,
        locationId: item.actualLocationId
      }));

      const response = await incomingService.updateAddressing(parseInt(id!), { items: updates });
      
      if (response.success) {
        showToast('Endereçamento concluído com sucesso!', 'success');
        // Sempre redirecionar para o dashboard após endereçar
        navigate('/erp/stock/incoming');
      } else {
        showToast(response.message || 'Erro ao concluir endereçamento', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao concluir endereçamento:', error);
      showToast(error.response?.data?.message || 'Erro ao concluir endereçamento', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredItems = items.filter(i => 
    (i.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (i.sku?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  const addressedCount = items.filter(i => i.isAddressed).length;
  const progress = items.length > 0 ? (addressedCount / items.length) * 100 : 0;

  if (loading) {
    return (
      <div className="incoming-addressing-loading">
        <Loader2 size={32} className="incoming-addressing-spinner" />
        <p>Carregando dados para endereçamento...</p>
      </div>
    );
  }

  return (
    <div className="incoming-addressing">
      {/* Header */}
      <div className="incoming-addressing-header">
        <div className="incoming-addressing-header-left">
          <button 
            onClick={() => navigate('/erp/stock/incoming')} 
            className="incoming-addressing-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-addressing-title-row">
              <h1 className="incoming-addressing-title">
                Endereçamento {invoiceEntry?.number || id}
              </h1>
              <span className="incoming-addressing-badge">Put-away</span>
            </div>
            <p className="incoming-addressing-subtitle">
              Alocação Física no Armazém
            </p>
          </div>
        </div>

        <div className="incoming-addressing-header-actions">
          <button 
            onClick={handleFinish}
            disabled={isProcessing || addressedCount < items.length}
            className="incoming-addressing-finish-btn"
            title={addressedCount < items.length ? 'Todos os itens devem ser endereçados' : ''}
          >
            {isProcessing ? (
              <Loader2 className="incoming-addressing-spinner" size={16} />
            ) : (
              <Save size={16} />
            )}
            Concluir Endereçamento
          </button>
        </div>
      </div>

      <div className="incoming-addressing-grid">
        {/* Items List */}
        <div className="incoming-addressing-items">
          {/* Search Bar */}
          <div className="incoming-addressing-search">
            <Search size={18} className="incoming-addressing-search-icon" />
            <input 
              type="text"
              placeholder="Buscar produto ou SKU para endereçar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="incoming-addressing-search-input"
            />
          </div>

          <div className="incoming-addressing-items-list">
            {filteredItems.length === 0 ? (
              <div className="incoming-addressing-empty">
                <Package size={48} />
                <p>Nenhum item encontrado</p>
              </div>
            ) : (
              filteredItems.map((item, idx) => (
                <div 
                  key={item.id} 
                  className={`incoming-addressing-item ${item.isAddressed ? 'incoming-addressing-item-addressed' : ''}`}
                >
                  <div className="incoming-addressing-item-content">
                    <div className="incoming-addressing-item-info">
                      <div className="incoming-addressing-item-icon">
                        <Package size={24} />
                      </div>
                      <div>
                        <p className="incoming-addressing-item-sku">{item.sku}</p>
                        <h4 className="incoming-addressing-item-name">{item.product_name}</h4>
                        <p className="incoming-addressing-item-details">
                          Qtd: {item.quantity} un
                          {item.lot && ` • Lote: ${item.lot}`}
                          {item.expiry && ` • Validade: ${new Date(item.expiry).toLocaleDateString('pt-BR')}`}
                        </p>
                      </div>
                    </div>

                    <div className="incoming-addressing-item-location">
                      <div className="incoming-addressing-item-location-header">
                        <label className="incoming-addressing-item-location-label">
                          Localização Física
                        </label>
                        {item.suggestedLocation && (
                          <button 
                            onClick={() => handleUseSuggested(item.id)}
                            className="incoming-addressing-item-suggest-btn"
                          >
                            Usar Sugestão: {item.suggestedLocation}
                          </button>
                        )}
                      </div>
                      <div className="incoming-addressing-item-location-input-wrapper">
                        <MapPin 
                          size={14} 
                          className={`incoming-addressing-item-location-icon ${item.isAddressed ? 'incoming-addressing-item-location-icon-active' : ''}`}
                        />
                        <input 
                          type="text" 
                          value={item.actualLocation}
                          onChange={(e) => handleUpdateLocation(item.id, e.target.value)}
                          onBlur={(e) => {
                            // Validar quando o campo perde o foco
                            const code = e.target.value.toUpperCase().trim();
                            if (code && !locations.find(loc => loc.code === code)) {
                              // Se o código não existe, limpar
                              handleUpdateLocation(item.id, '');
                            }
                          }}
                          placeholder="Ex: A1-02-B"
                          list={`location-list-${item.id}`}
                          className={`incoming-addressing-item-location-input ${item.isAddressed ? 'incoming-addressing-item-location-input-addressed' : ''}`}
                        />
                        <datalist id={`location-list-${item.id}`}>
                          {locations.map(loc => (
                            <option key={loc.id} value={loc.code} />
                          ))}
                        </datalist>
                        {item.isAddressed && (
                          <CheckCircle2 
                            size={16} 
                            className="incoming-addressing-item-location-check" 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="incoming-addressing-sidebar">
          <div className="incoming-addressing-progress-card">
            <h3 className="incoming-addressing-progress-title">
              <Box size={16} /> Progresso do Put-away
            </h3>

            <div className="incoming-addressing-progress-content">
              <div className="incoming-addressing-progress-stats">
                <div className="incoming-addressing-progress-numbers">
                  <p className="incoming-addressing-progress-count">
                    {addressedCount} <span className="incoming-addressing-progress-total">/ {items.length}</span>
                  </p>
                  <p className="incoming-addressing-progress-percent">
                    {Math.round(progress)}% Concluído
                  </p>
                </div>
                <div className="incoming-addressing-progress-bar">
                  <div 
                    className="incoming-addressing-progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="incoming-addressing-progress-info">
                <Info size={18} className="incoming-addressing-progress-info-icon" />
                <p className="incoming-addressing-progress-info-text">
                  O sistema sugere a localização baseada na curva ABC do produto e no espaço disponível no momento do lançamento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default IncomingAddressingPage;
